import React, { useCallback, useMemo } from "react";
import { Patient } from "@shared/models";
import { getFullName } from "@shared/utils";
import { NameOfChildRoutes, NamesOfParentRoutes } from "@shared/constants";
import { generatePath, useNavigate } from "react-router";
import { Icon } from "@shared/components";

import "./index.scss";
import { NewPatientCardStablePeriodInfo } from "../NewPatientCardStablePeriodInfo";

interface NewPatientCardProps {
  patient: Patient;
}

const NewPatientCard = (props: NewPatientCardProps) => {
  const { patient } = props;
  const navigate = useNavigate();

  const stablePeriodRRValue = useMemo(() => {
    return patient.patient_settings?.rr_stable_period || 0;
  }, [patient]);

  const stablePeriodHRValue = useMemo(() => {
    return patient.patient_settings?.hr_stable_period || 0;
  }, [patient]);

  const stablePeriodRRAvgValue = useMemo(() => {
    return patient.patient_settings?.rr_current_avg || 0;
  }, [patient]);

  const stablePeriodHRAvgValue = useMemo(() => {
    return patient.patient_settings?.hr_current_avg || 0;
  }, [patient]);

  const stablePeriodRRPercentage = useMemo(() => {
    if (!stablePeriodRRValue || (!stablePeriodRRAvgValue && stablePeriodRRValue !== stablePeriodRRAvgValue)) return 0;
    if (stablePeriodRRValue === stablePeriodRRAvgValue) return 0;
    if (stablePeriodRRValue > stablePeriodRRAvgValue) {
      const percentage = 100 - (stablePeriodRRAvgValue / stablePeriodRRValue) * 100;
      return Number(percentage.toFixed(2));
    }

    const percentage = 100 - (stablePeriodRRValue / stablePeriodRRAvgValue) * 100;
    return Number(percentage.toFixed(2));
  }, [stablePeriodRRValue, stablePeriodRRAvgValue]);

  const stablePeriodHRPercentage = useMemo(() => {
    if (!stablePeriodHRValue || (!stablePeriodHRAvgValue && stablePeriodHRValue !== stablePeriodHRAvgValue)) return 0;
    if (stablePeriodHRValue === stablePeriodHRAvgValue) return 0;
    if (stablePeriodHRValue > stablePeriodHRAvgValue) {
      const percentage = 100 - (stablePeriodHRAvgValue / stablePeriodHRValue) * 100;
      return Number(percentage.toFixed(2));
    }

    const percentage = 100 - (stablePeriodHRValue / stablePeriodHRAvgValue) * 100;
    return Number(percentage.toFixed(2));
  }, [stablePeriodHRValue, stablePeriodHRAvgValue]);

  const handlePatientClick = useCallback(() => {
    navigate(
      generatePath(
        `${NamesOfParentRoutes.MANAGEMENT}${NameOfChildRoutes.MANAGEMENT.PATIENTS.ROOT}${NameOfChildRoutes.MANAGEMENT.PATIENTS.DETAILS}`,
        { patient_id: String(patient.id) },
      ),
    );
  }, [patient, navigate]);

  return (
    <div className="new-patients-card panel" onClick={() => handlePatientClick()}>
      <div className="new-patients-card-row patient-information">
        <div className="patient-name">
          <div className="patient-name-icon">
            <Icon type="person" />
          </div>
          <div className="patient-name-info">
            <span>{getFullName(patient)}</span>
          </div>
        </div>
      </div>
      <div className="new-patients-card-stable-period">
        <NewPatientCardStablePeriodInfo
          signalName="HR"
          stablePeriod={stablePeriodHRValue}
          stableAvgPeriod={stablePeriodHRAvgValue}
          stablePeriodPercentage={stablePeriodHRPercentage}
        />
        <NewPatientCardStablePeriodInfo
          signalName="RR"
          stablePeriod={stablePeriodRRValue}
          stableAvgPeriod={stablePeriodRRAvgValue}
          stablePeriodPercentage={stablePeriodRRPercentage}
        />
      </div>
    </div>
  );
};

export default NewPatientCard;
